import { ComponentBase } from "../core/ComponentBase.js";

export class FileUpload extends ComponentBase {
  static styles = `
  .file-upload-container {
    border: 2px dashed #ced4da; /* Bootstrap-style dashed border */
    padding: 20px;
    border-radius: var(--ab-radius-sm);
    text-align: center;
    cursor: pointer;
  }
  .file-upload-container:hover {
    background-color: #f8f9fa;
  }
  .file-list {
    list-style: none;
    padding: 0;
  }
  .file-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 8px 0;
    border-bottom: 1px solid #eee;
  }
  .file-name {
    flex: 1;
    margin-right: 10px;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }
  .file-progress-bar {
    width: 150px; /* Adjust as needed */
    height: 10px;
  }
  .file-remove-btn {
    color: red;
    cursor: pointer;
    border: none;
    background: none;
    padding: 0;
    font-size: var(--ab-text-md);
  }
  .overall-progress-bar {
    margin-top: 10px;
    width: 100%;
    height: 15px;
    border: 1px solid #ddd;
  }
  .hidden {
    display: none;
  }
`;
  constructor(config) {
    super(config);
    this.uploadKey = null;
    this.files = [];
    this.uploadUrl = null;
    this.fileListContainer = null;
    this.dropZone = null;
    this.fileInput = null; // Keep the file input for openFile()
    this.overallProgressBar = null;
    this.applyStyles();
    this.container = document.createElement("div");
    this.rendered = false;
    this.container.classList.add('field-container');
    this.filesChangedEventName = `filesChanged`;


  }

  static getDefaultConfig() {
    return {
      getUploadKey: null, // REQUIRED: Async function to get the upload key
      allowedFileTypes: [],
      maxFiles: 5,
      maxFileSize: 10 * 1024 * 1024, // 10 MB
      label: 'Upload Files',
      description: 'Drag and drop files here, or click to browse.',
      showLabel: false,
      hidden: false,
      autoUpload: true,
    };
  }

  async initialize() {
    await super.initialize();
    this.updateHiddenState(); // Apply initial hidden state
  }


  async render() {

    if (!this.rendered) {
      this.rendered = true;

      const container = this.container;
      container.innerHTML = ""; // Clear previous content

      if (this.config.showLabel) {
        const label = document.createElement("label");
        label.classList.add("form-label");
        label.textContent = this.config.label;
        container.appendChild(label);

        const description = document.createElement("small");
        description.className = "form-text text-muted";
        description.textContent = this.config.description;
        container.appendChild(description);
      }

      this.dropZone = document.createElement('div');
      this.dropZone.className = 'file-upload-container';
      this.dropZone.innerHTML = 'Drop files here, or click to browse.';
      this.dropZone.addEventListener('click', this.handleFileBrowseClick.bind(this));
      this.dropZone.addEventListener('dragover', this.handleDragOver.bind(this));
      this.dropZone.addEventListener('drop', this.handleFileDrop.bind(this));
      container.appendChild(this.dropZone);

      this.fileInput = document.createElement('input');
      this.fileInput.id = this.config.instanceId;
      this.fileInput.type = 'file';
      this.fileInput.multiple = this.config.maxFiles > 1;
      this.fileInput.style.display = 'none';
      this.fileInput.addEventListener('change', this.handleFileSelect.bind(this));
      container.appendChild(this.fileInput);

      this.fileListContainer = document.createElement('ul');
      this.fileListContainer.className = 'file-list';
      container.appendChild(this.fileListContainer);

      this.overallProgressBar = document.createElement('div');
      this.overallProgressBar.className = 'progress overall-progress-bar';
      this.overallProgressBar.innerHTML = '<div class="progress-bar" role="progressbar" style="width: 0%;" aria-valuenow="0" aria-valuemin="0" aria-valuemax="100"></div>';
      this.overallProgressBar.style.display = 'none'; // Initially hidden
      container.appendChild(this.overallProgressBar);

      this.updateHiddenState(); // Apply hidden state on render
    }
    return this.container;
  }

  updateHiddenState() {
    if (this.config.hidden) {
      this.container.classList.add('hidden');
    } else {
      this.container.classList.remove('hidden');
    }
  }


  handleFileBrowseClick(event) {
    event.preventDefault();
    this.openFile(); // Use the openFile method
  }

  handleDragOver(event) {
    event.preventDefault();
    this.dropZone.classList.add('dragover');
  }

  async getAllFilesFromDataTransfer(dataTransfer) {
    const files = [];
    const queue = [];

    if (dataTransfer.items) {
      for (let i = 0; i < dataTransfer.items.length; i++) {
        const item = dataTransfer.items[i];
        if (item.kind === 'file') {
          const entry = typeof item.webkitGetAsEntry === 'function' ? item.webkitGetAsEntry() : null;
          if (entry) {
            queue.push(entry);
          } else {
            const file = item.getAsFile();
            if (file) files.push(file);
          }
        }
      }
    } else if (dataTransfer.files) {
      for (let i = 0; i < dataTransfer.files.length; i++) {
        files.push(dataTransfer.files[i]);
      }
    }

    if (queue.length > 0) {
      const readEntry = (entry) => {
        return new Promise((resolve) => {
          if (entry.isFile) {
            entry.file((file) => {
              file.relativeFilePath = entry.fullPath.replace(/^\//, '');
              files.push(file);
              resolve();
            }, () => resolve());
          } else if (entry.isDirectory) {
            const reader = entry.createReader();
            const readAllEntries = () => {
              reader.readEntries((entries) => {
                if (entries.length === 0) {
                  resolve();
                } else {
                  const promises = entries.map(e => readEntry(e));
                  Promise.all(promises).then(() => readAllEntries()).catch(() => resolve());
                }
              }, () => resolve());
            };
            readAllEntries();
          } else {
            resolve();
          }
        });
      };

      const promises = queue.map(entry => readEntry(entry));
      await Promise.all(promises);
    }

    return files;
  }

  async handleFileDrop(event) {
    event.preventDefault();
    this.dropZone.classList.remove('dragover');
    const files = await this.getAllFilesFromDataTransfer(event.dataTransfer);
    this.addFiles(files);
  }


  handleFileSelect(event) {
    const files = event.target.files;
    this.addFiles(files);
  }

  openFile() {
    this.fileInput.click(); // Trigger the file input
  }

  async addFiles(files) {

    if (this.config.maxFiles === 1 && files.length > 0) {
      this.files = [];
    }

    if (this.files.length + files.length > this.config.maxFiles) {
      this.appClient.notifier.alert(`You can upload a maximum of ${this.config.maxFiles} files.`, { type: "warning" });
      return;
    }

    for (const file of files) {
      if (this.config.allowedFileTypes.length > 0 && !this.config.allowedFileTypes.includes(file.type)) {
        this.appClient.notifier.alert(`File type not allowed: ${file.name}`, { type: "warning" });
        continue;
      }

      if (file.size > this.config.maxFileSize) {
        this.appClient.notifier.alert(`File too large: ${file.name}.  Maximum size is ${this.config.maxFileSize / 1024 / 1024}MB.`, { type: "warning" });
        continue;
      }
      const fileId = this.generateUniqueId();
      this.files.push({ file, progress: 0, status: 'pending', fileId });
      this.renderFileList();
    }

    this.emit(this.filesChangedEventName, this.files);
    if (this.config.autoUpload) {
      await this.startUpload();
    }
  }

  async startUpload() {
    try {
      // --- THIS IS THE FIX ---
      // Check for the getUploadKey function in both the root config and the props object
      // for backward compatibility and flexibility.
      const getUploadKeyFn = this.config.getUploadKey || this.config.props?.getUploadKey;
      if (typeof getUploadKeyFn !== 'function') {
        throw new Error("The 'getUploadKey' function was not provided in the component's configuration.");
      }
      const uploadInfo = await getUploadKeyFn();
      // --- END OF FIX ---

      if (!uploadInfo) { // Gracefully handle if key generation is aborted
        this.log.warn("getUploadKey returned null. Aborting upload.");
        return null; // Return null to signal the parent that upload was aborted
      }
      const { key, server, url } = uploadInfo;
      this.uploadKey = key;

      let finalServer = server || '';
      if (typeof window !== 'undefined' && window.location && window.location.origin) {
        const origin = window.location.origin;
        if (origin.startsWith('http://') || origin.startsWith('https://')) {
          if (finalServer) {
            try {
              const serverOrigin = new URL(finalServer).origin;
              if (serverOrigin !== origin) {
                this.log.warn(`Server config URL (${finalServer}) differs from page origin (${origin}). Using relative path for upload to avoid CORS.`);
                finalServer = '';
              }
            } catch (err) {
              this.log.error("Failed to parse server URL:", err);
            }
          }
        }
      }
      this.uploadUrl = finalServer + url;
    } catch (error) {
      this.log.error("Failed to get a new upload key:", error);
      this.appClient.notifier.error("Could not prepare the upload. Please try again.");
      throw error; // Re-throw so the calling form's submit handler can catch it
    }

    if (this.files.length === 0) {
      this.appClient.notifier.notify("No files to upload.", { type: "info" });
      return null;
    }

    this.overallProgressBar.style.display = 'block';
    this.emit('uploadStarted');

    try {
      for (const fileItem of this.files) {
        if (fileItem.status === 'pending') {
          await this.uploadSingleFile(fileItem);
        }
      }
    } catch (error) {
      this.emit('uploadFailed', error);
      throw error;
    }

    this.emit('uploadCompleted');
    return this.uploadKey; // Return the key on successful upload
  }


  async uploadSingleFile(fileItem) {
    // ... (your existing uploadSingleFile method) ...
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', this.uploadUrl);
      xhr.setRequestHeader('X-Requested-With', 'XMLHttpRequest');

      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) {
          fileItem.progress = Math.round((event.loaded / event.total) * 100);
          this.updateFileProgress(fileItem);
          this.updateOverallProgress();
        }
      };

      xhr.onload = async () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          const response = JSON.parse(xhr.response);

          if (response.success) {
            fileItem.status = "uploaded";
            this.updateFileProgress(fileItem);
            resolve();
          }
          else {
            fileItem.status = 'error';
            this.updateFileProgress(fileItem);
            reject(new Error(`Upload failed with status ${xhr.status}: ${xhr.responseText}`));
          }
        } else {
          this.log.error(`File upload failed: ${fileItem.file.name}`, xhr.statusText);
          fileItem.status = 'error';
          this.updateFileProgress(fileItem);
          reject(new Error('Network error during file upload'));
        }
      };

      xhr.onerror = () => {
        this.log.error(`File upload error: ${fileItem.file.name}`);
        fileItem.status = 'error';
        this.updateFileProgress(fileItem);
        reject(new Error('Network error during file upload'));
      };

      const formData = new FormData();
      formData.append('files', fileItem.file);

      xhr.send(formData);
    });
  }

  generateUniqueId() {
    return Date.now().toString(36) + Math.random().toString(36).substr(2, 9);
  }

  renderFileList() {
    this.fileListContainer.innerHTML = '';

    this.files.forEach((fileItem, index) => {
      const listItem = document.createElement('li');
      listItem.className = 'file-item';
      listItem.innerHTML = `
                    <span class="file-name">${fileItem.file.name}</span>
                    <div class="progress file-progress-bar">
                        <div class="progress-bar" role="progressbar" style="width: ${fileItem.progress}%;" aria-valuenow="${fileItem.progress}" aria-valuemin="0" aria-valuemax="100"></div>
                    </div>
                    <button class="file-remove-btn" data-index="${index}"><i class="bi bi-x"></i></button>
                `;
      listItem.querySelector('.file-remove-btn').addEventListener('click', () => {
        this.removeFile(index);
      });

      this.fileListContainer.appendChild(listItem);
    });
  }

  async removeFile(index) {
    const fileItem = this.files[index];
    this.files.splice(index, 1);
    this.renderFileList();
    this.updateOverallProgress();
    this.emit(this.filesChangedEventName, this.files);
  }

  updateFileProgress(fileItem) {
    const fileIndex = this.files.indexOf(fileItem);
    if (fileIndex > -1) {
      const progressBar = this.fileListContainer.children[fileIndex].querySelector('.progress-bar');
      if (progressBar) {
        progressBar.style.width = `${fileItem.progress}%`;
        progressBar.setAttribute('aria-valuenow', fileItem.progress);
        if (fileItem.status === 'error') {
          progressBar.classList.add('bg-danger');
        }
      }
    }
  }

  updateOverallProgress() {
    const totalFiles = this.files.length;
    if (totalFiles === 0) {
      this.overallProgressBar.style.display = 'none';
      return;
    }

    const totalProgress = this.files.reduce((sum, fileItem) => sum + fileItem.progress, 0);
    const overallProgress = Math.round(totalProgress / totalFiles);
    const progressBar = this.overallProgressBar.querySelector('.progress-bar');
    progressBar.style.width = `${overallProgress}%`;
    progressBar.setAttribute('aria-valuenow', overallProgress);
  }

  getValue() {
    return this.uploadKey ? this.uploadKey : '';
  }

  setValue(value) {
    this.clear();
    this.log.info("setValue is not supported for FileUpload component");
  }

  clear() {
    this.uploadUrl = null;
    this.uploadKey = null;
    this.files = [];
    this.renderFileList();
    this.updateOverallProgress();
  }

  setConfig(newConfig) {
    this.config = { ...this.config, ...newConfig };
    this.updateHiddenState();
  }

  destroy() {
    super.destroy();
  }
}